import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';

export type LoaderTheme = 'light' | 'dark';

/**
 * Theme state for loaders. Observes `data-theme` on the element passed to
 * observe(). Provided per loader by default so each loader follows its local
 * theme context (e.g. dark section vs light section). Provide at a parent
 * (e.g. a themed container) to share one service and one observer for all
 * loaders in that subtree.
 */
@Injectable()
export class LoaderThemeService {
  readonly theme = signal<LoaderTheme>('light');

  private readonly platformId = inject(PLATFORM_ID);
  private observedElement: Element | null = null;
  private observer: MutationObserver | null = null;

  /**
   * Observe `data-theme` on this element. Call with the loader host's closest
   * [data-theme] or document.documentElement. Re-applies theme synchronously on
   * each call; switches the observer when the target element changes.
   */
  observe(element: Element): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const updateTheme = (): void => {
      const value = element.getAttribute('data-theme');
      this.theme.set(value === 'dark' ? 'dark' : 'light');
    };

    updateTheme();

    if (this.observedElement === element) {
      return;
    }

    this.observer?.disconnect();
    this.observedElement = element;
    this.observer = new MutationObserver(updateTheme);
    this.observer.observe(element, { attributes: true, attributeFilter: ['data-theme'] });
  }
}
